/**
 * ============================================================
 * MAP LAYER STYLES
 * Edit this object to tweak all visual properties (color,
 * opacity, weight, dash pattern, pane) from a single place.
 * ============================================================
 *
 * Color conventions
 *   protectionHigh → mustard / golden tones  (warm but not orange/red)
 *   protectionLow  → blue-green / teal tones (cool, clearly distinct from High)
 *   ctr / atz      → blue / purple            (airspace)
 *   naturalPark    → green
 *   harbour        → cyan
 *   structure      → deep orange
 *   other          → deep purple
 */
export const MAP_STYLES = {

  // ── Catch-all / restricted ────────────────────────────────
  other:        { color: '#4A148C', fillOpacity: 0.15, weight: 1, dash: '5, 5', pane: 'pane-other' },

  // ── Controlled airspace ───────────────────────────────────
  ctr:          { color: '#1565C0', fillOpacity: 0.22, weight: 2, dash: null,   pane: 'pane-ctr-atz' },
  atz:          { color: '#7B1FA2', fillOpacity: 0.22, weight: 2, dash: null,   pane: 'pane-ctr-atz' },

  // ── Protection HIGH — mustard / golden family ─────────────
  //    Each infra type gets a distinct shade in the yellow-gold-khaki range.
  protectionHigh: {
    default:   { color: '#C9A000', fillOpacity: 0.18, weight: 2, dash: '5, 5', pane: 'pane-protection-high' },
    airport:   { color: '#B8860B', fillOpacity: 0.18, weight: 2, dash: '5, 5', pane: 'pane-protection-high' },
    aerodrome: { color: '#DAA520', fillOpacity: 0.18, weight: 2, dash: '5, 5', pane: 'pane-protection-high' },
    airfield:  { color: '#8D6500', fillOpacity: 0.18, weight: 2, dash: '5, 5', pane: 'pane-protection-high' },
    heliport:  { color: '#E5C100', fillOpacity: 0.18, weight: 2, dash: '5, 5', pane: 'pane-protection-high' },
  },

  // ── Protection LOW — blue-green / teal family ─────────────
  //    Distinctly cool so High/Low zones never look the same.
  protectionLow: {
    default:   { color: '#006064', fillOpacity: 0.28, weight: 2, dash: '5, 5', pane: 'pane-protection-low' },
    airport:   { color: '#004D40', fillOpacity: 0.28, weight: 2, dash: '5, 5', pane: 'pane-protection-low' },
    aerodrome: { color: '#00838F', fillOpacity: 0.28, weight: 2, dash: '5, 5', pane: 'pane-protection-low' },
    airfield:  { color: '#0277BD', fillOpacity: 0.28, weight: 2, dash: '5, 5', pane: 'pane-protection-low' },
    heliport:  { color: '#00695C', fillOpacity: 0.28, weight: 2, dash: '5, 5', pane: 'pane-protection-low' },
  },

  // ── Natural environment ───────────────────────────────────
  naturalPark:  { color: '#2E7D32', fillOpacity: 0.18, weight: 2, dash: null,   pane: 'pane-nat-parks' },
  harbour:      { color: '#00838F', fillOpacity: 0.18, weight: 2, dash: null,   pane: 'pane-harbours-tracks' },

  // ── Infrastructure / built ────────────────────────────────
  structure:    { color: '#E65100', fillOpacity: 0.22, weight: 2, dash: null,   pane: 'pane-structures' },
  innerZone:    { color: '#546E7A', fillOpacity: 0.06, weight: 1, dash: '5, 5', pane: 'pane-inner-zones' },
  helipad:      { color: '#E65100', fillOpacity: 0.18, weight: 2, dash: null,   pane: 'pane-structures' },

  // ── Urban areas ───────────────────────────────────────────
  urbanAreaPopulation: { color: '#BF360C', fillOpacity: 0.18, weight: 2, dash: null, pane: 'pane-urban-areas' },
  urbanAreaRegistry:   { color: '#1565C0', fillOpacity: 0.18, weight: 2, dash: null, pane: 'pane-urban-areas' },

  // ── Track network ─────────────────────────────────────────
  trackPalette: ['#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A', '#98D8C8', '#F7DC6F', '#BB8FCE', '#85C1E2', '#F8B739', '#52B788'],
  trackWeight:  3,
  trackPane:    'pane-harbours-tracks',
} as const;

/** Map a backend infra type string to the correct style sub-key. */
export function infraTypeKey(
  infraType: string,
): 'airport' | 'aerodrome' | 'airfield' | 'heliport' | 'default' {
  const t = (infraType ?? '').toLowerCase();
  if (t.includes('airport'))   { return 'airport'; }
  if (t.includes('aerodrome')) { return 'aerodrome'; }
  if (t.includes('airfield'))  { return 'airfield'; }
  if (t.includes('heliport'))  { return 'heliport'; }
  return 'default';
}
